import { createContext, useEffect, useReducer } from "react";
import type { FC, ReactNode } from "react";
import jwtDecode from "jwt-decode";
import axios from "../utils/axios";
import "react-toastify/dist/ReactToastify.css";
import { SplashPage } from "../components/splash";
import {
  loginAction,
  registerAction,
  getUserInfo,
} from "../actions/user";
import { Backend, Frontend } from "shared-types";

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: Backend.T_User | null;
  totalCounter?: Frontend.T_TotalCounters;
}

interface AuthContextValue extends AuthState {
  method: "JWT";
  login: (formData: any) => Promise<any>;
  logout: () => void;
  register: (formData: any) => Promise<any>;
  dispatchUser: (user: Backend.T_User) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: "INITIALIZE";
  payload: {
    isAuthenticated: boolean;
    user: Backend.T_User | null;
  };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: Backend.T_User;
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type RegisterAction = {
  type: "REGISTER";
  payload: {
    user: Backend.T_User;
  };
};

type TotalCounterAction = {
  type: "TOTAL_COUNTER";
  payload: {
    totalCounter: Frontend.T_TotalCounters;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | TotalCounterAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const isValidToken = (accessToken: string): boolean => {
  if (!accessToken) {
    return false;
  }

  const decoded: any = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decoded.expires > currentTime;
};

export const setSession = (accessToken: string | null): void => {
  if (accessToken) {
    window.localStorage.setItem("accessToken", accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("userinfo");
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case "INITIALIZE": {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    }
    case "LOGIN": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case "TOTAL_COUNTER": {
      const { totalCounter } = action.payload;
      return {
        ...state,
        totalCounter,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    case "REGISTER": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: "JWT",
  login: () => Promise.resolve(-1),
  logout: () => {
    return false;
  },
  register: () => Promise.resolve(false),
  dispatchUser: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const login = async (formData: Backend.T_SignInForm) => {
    try {
      const response = await loginAction(formData);
      if (response.status === true) {
        setSession(response.data.token);
        await getUserInfo().then((response) => {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: response.data.userInfo,
            },
          });
        }).catch(console.warn);
      }
      return response;
    } catch (error) {
      console.error(error);
      return { status: false, message: "Something went wrong!" };
    }
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: "LOGOUT" });
    window.location.href = "/login";
  };

  const dispatchUser = (user: Backend.T_User) => {
    dispatch({
      type: "INITIALIZE",
      payload: {
        isAuthenticated: true,
        user: user,
      },
    });
  };

  const register = async (formData: Backend.T_SignUpForm) => {
    try {
      const response: any = await registerAction(formData);
      const userDetails = response?.data || {};
      if (userDetails?.data?.user?.isFailed) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: response.data.user,
          },
        });
      } else if (response.status) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: true,
            user: response.data.user,
          },
        });
      }
      return response;
    } catch (error) {
      return {
        status: false,
        message: "Something went wrong!",
      };
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          //const userinfo = window.localStorage.getItem("userinfo");
          //if(userinfo == undefined || userinfo == null){
          await getUserInfo().then((response) => {
            const accept = window.localStorage.getItem("acceptCookie");
            if (accept) {
              window.localStorage.setItem(
                "userinfo",
                JSON.stringify(response.data.userInfo)
              );
            }
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: true,
                user: response.data.userInfo,
              },
            });
          }).catch(() => logout());
          // } else {
          //   dispatch({
          //     type: "INITIALISE",
          //     payload: {
          //       isAuthenticated: true,
          //       user: JSON.parse(userinfo),
          //     },
          //   });
          // }
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  if (!state.isInitialized) {
    return <SplashPage />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
        dispatchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
